/**
 * /sign-in/access — where mariejois sends a browser after the identity
 * provider succeeded but Specra has no usable account. Public on purpose:
 * the visitor is not signed in. Query params are untrusted text.
 */ import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { SpecraButton, SpecraCard, SpecraCardContent, SpecraHeading, SpecraLink, SpecraText, SpecraWordmark, specraButtonVariants } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useSearchParams } from 'react-router-dom';
import { cn } from '../../lib/utils';
import { copyToClipboard } from '../../utils/downloadUtils';
import { adminInviteMessage, emailDomain, orgLabel, parseAccessEmail, parseAccessOrg, parseAccessProvider, parseAccessReason } from './accessParams';
/**
 * A federal building with its flag up and a security checkpoint out front: a
 * visitor badge taps the reader, the reader blinks amber (not yet, rather than
 * denied) and the badge drifts back. The motion loops gently and stops for
 * anyone who prefers reduced motion. Drawn in the brand colour
 * (currentColor), so it follows light and dark mode.
 */ const CHECKPOINT_MOTION = `
@keyframes access-flag { 0%, 100% { transform: skewY(0deg) scaleX(1); } 50% { transform: skewY(-7deg) scaleX(0.94); } }
@keyframes access-badge {
  0%, 18% { transform: translate(0, 0) rotate(-6deg); }
  38%, 58% { transform: translate(7px, -2px) rotate(0deg); }
  80%, 100% { transform: translate(0, 0) rotate(-6deg); }
}
@keyframes access-light { 0%, 36%, 62%, 100% { opacity: 0.25; } 42%, 54% { opacity: 1; } }
@keyframes access-ring { 0%, 40% { opacity: 0; transform: scale(0.6); } 48% { opacity: 0.55; } 64%, 100% { opacity: 0; transform: scale(2.2); } }
.access-flag { transform-box: fill-box; transform-origin: left center; animation: access-flag 2.6s ease-in-out infinite; }
.access-badge { transform-box: fill-box; transform-origin: center; animation: access-badge 3.6s ease-in-out infinite; }
.access-light { animation: access-light 3.6s ease-in-out infinite; }
.access-ring { transform-box: fill-box; transform-origin: center; animation: access-ring 3.6s ease-out infinite; }
@media (prefers-reduced-motion: reduce) {
  .access-flag, .access-badge, .access-light, .access-ring { animation: none; }
  .access-light { opacity: 1; }
  .access-ring { opacity: 0; }
}
`;
function AccessIllustration() {
    const columns = [
        74,
        94,
        114,
        134,
        154
    ];
    return /*#__PURE__*/ _jsxs("svg", {
        "aria-hidden": "true",
        className: "h-44 w-60 text-brand",
        fill: "none",
        viewBox: "0 0 240 176",
        children: [
            /*#__PURE__*/ _jsx("style", {
                children: CHECKPOINT_MOTION
            }),
            /*#__PURE__*/ _jsx("defs", {
                children: /*#__PURE__*/ _jsxs("linearGradient", {
                    id: "access-sky",
                    x1: "0",
                    x2: "0",
                    y1: "0",
                    y2: "1",
                    children: [
                        /*#__PURE__*/ _jsx("stop", {
                            offset: "0",
                            stopColor: "hsl(var(--brand-muted))"
                        }),
                        /*#__PURE__*/ _jsx("stop", {
                            offset: "1",
                            stopColor: "hsl(var(--brand-muted))",
                            stopOpacity: "0.2"
                        })
                    ]
                })
            }),
            /*#__PURE__*/ _jsx("circle", {
                cx: "112",
                cy: "86",
                fill: "url(#access-sky)",
                r: "72"
            }),
            /*#__PURE__*/ _jsx("ellipse", {
                cx: "120",
                cy: "156",
                fill: "currentColor",
                opacity: "0.07",
                rx: "104",
                ry: "10"
            }),
            /*#__PURE__*/ _jsxs("g", {
                transform: "translate(-16 0)",
                children: [
                    /*#__PURE__*/ _jsx("path", {
                        d: "M120 34 V8",
                        stroke: "currentColor",
                        strokeLinecap: "round",
                        strokeWidth: "2"
                    }),
                    /*#__PURE__*/ _jsxs("g", {
                        className: "access-flag",
                        children: [
                            /*#__PURE__*/ _jsx("rect", {
                                fill: "currentColor",
                                height: "13",
                                rx: "1",
                                width: "22",
                                x: "121",
                                y: "9"
                            }),
                            /*#__PURE__*/ _jsx("path", {
                                d: "M121 13.5 H143 M121 18 H143",
                                stroke: "var(--surface-card)",
                                strokeWidth: "1.6"
                            }),
                            /*#__PURE__*/ _jsx("rect", {
                                fill: "var(--surface-card)",
                                height: "5",
                                opacity: "0.9",
                                width: "8",
                                x: "121",
                                y: "9"
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsx("path", {
                        d: "M62 58 L120 32 L178 58 Z",
                        fill: "var(--surface-card)",
                        stroke: "currentColor",
                        strokeLinejoin: "round",
                        strokeWidth: "3"
                    }),
                    /*#__PURE__*/ _jsx("circle", {
                        cx: "120",
                        cy: "49",
                        fill: "currentColor",
                        opacity: "0.35",
                        r: "4"
                    }),
                    /*#__PURE__*/ _jsx("rect", {
                        fill: "currentColor",
                        height: "8",
                        rx: "1.5",
                        width: "124",
                        x: "58",
                        y: "58"
                    }),
                    columns.map((x)=>/*#__PURE__*/ _jsxs("g", {
                            children: [
                                /*#__PURE__*/ _jsx("rect", {
                                    fill: "var(--surface-card)",
                                    height: "50",
                                    stroke: "currentColor",
                                    strokeWidth: "2.5",
                                    width: "12",
                                    x: x,
                                    y: "68"
                                }),
                                /*#__PURE__*/ _jsx("path", {
                                    d: `M${x + 4} 72 V114 M${x + 8} 72 V114`,
                                    stroke: "currentColor",
                                    strokeWidth: "1",
                                    opacity: "0.3"
                                })
                            ]
                        }, x)),
                    /*#__PURE__*/ _jsx("rect", {
                        fill: "currentColor",
                        height: "6",
                        rx: "1.5",
                        width: "132",
                        x: "54",
                        y: "118"
                    }),
                    /*#__PURE__*/ _jsx("rect", {
                        fill: "var(--surface-card)",
                        height: "6",
                        stroke: "currentColor",
                        strokeWidth: "2",
                        width: "144",
                        x: "48",
                        y: "124"
                    }),
                    /*#__PURE__*/ _jsx("rect", {
                        fill: "var(--surface-card)",
                        height: "6",
                        stroke: "currentColor",
                        strokeWidth: "2",
                        width: "156",
                        x: "42",
                        y: "130"
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs("g", {
                transform: "translate(10 0)",
                children: [
                    /*#__PURE__*/ _jsx("rect", {
                        fill: "currentColor",
                        height: "34",
                        rx: "2",
                        width: "5",
                        x: "207.5",
                        y: "116"
                    }),
                    /*#__PURE__*/ _jsx("rect", {
                        fill: "var(--surface-card)",
                        height: "26",
                        rx: "4",
                        stroke: "currentColor",
                        strokeWidth: "2.5",
                        width: "18",
                        x: "201",
                        y: "92"
                    }),
                    /*#__PURE__*/ _jsx("rect", {
                        fill: "currentColor",
                        height: "7",
                        opacity: "0.15",
                        rx: "1.5",
                        width: "10",
                        x: "205",
                        y: "98"
                    }),
                    /*#__PURE__*/ _jsx("circle", {
                        className: "access-ring",
                        cx: "210",
                        cy: "111",
                        fill: "hsl(var(--warning))",
                        r: "3.5"
                    }),
                    /*#__PURE__*/ _jsx("circle", {
                        className: "access-light",
                        cx: "210",
                        cy: "111",
                        fill: "hsl(var(--warning))",
                        r: "3"
                    }),
                    /*#__PURE__*/ _jsxs("g", {
                        className: "access-badge",
                        children: [
                            /*#__PURE__*/ _jsx("path", {
                                d: "M186 84 V92",
                                stroke: "currentColor",
                                strokeLinecap: "round",
                                strokeWidth: "1.8"
                            }),
                            /*#__PURE__*/ _jsx("rect", {
                                fill: "var(--surface-card)",
                                height: "30",
                                rx: "4",
                                stroke: "currentColor",
                                strokeWidth: "2.5",
                                width: "22",
                                x: "175",
                                y: "92"
                            }),
                            /*#__PURE__*/ _jsx("rect", {
                                fill: "currentColor",
                                height: "7",
                                rx: "1",
                                width: "22",
                                x: "175",
                                y: "92"
                            }),
                            /*#__PURE__*/ _jsx("circle", {
                                cx: "186",
                                cy: "106",
                                fill: "hsl(var(--brand-muted))",
                                r: "4.5",
                                stroke: "currentColor",
                                strokeWidth: "1.6"
                            }),
                            /*#__PURE__*/ _jsx("rect", {
                                fill: "currentColor",
                                height: "2.2",
                                opacity: "0.45",
                                rx: "1.1",
                                width: "12",
                                x: "180",
                                y: "114"
                            })
                        ]
                    })
                ]
            }),
            /*#__PURE__*/ _jsx("path", {
                d: "M44 44 l2.2 5.6 5.6 2.2 -5.6 2.2 -2.2 5.6 -2.2 -5.6 -5.6 -2.2 5.6 -2.2 Z",
                fill: "currentColor",
                opacity: "0.4"
            }),
            /*#__PURE__*/ _jsx("path", {
                d: "M196 34 l1.5 3.9 3.9 1.5 -3.9 1.5 -1.5 3.9 -1.5 -3.9 -3.9 -1.5 3.9 -1.5 Z",
                fill: "currentColor",
                opacity: "0.3"
            })
        ]
    });
}
function headlineFor(reason, org, domain) {
    const name = orgLabel(org);
    switch(reason){
        case 'no_account':
            return `You don't have access to ${name} yet`;
        case 'domain_not_allowed':
            return domain ? `${name} doesn't allow sign-ins from ${domain}` : `${name} doesn't allow sign-ins from that email domain`;
        case 'inactive':
            return `Your account in ${name} is turned off`;
        case 'provider_error':
            return "We couldn't finish signing you in";
        default:
            return "We couldn't sign you in";
    }
}
function AccessBody({ domain, email, org, reason }) {
    const name = orgLabel(org);
    switch(reason){
        case 'no_account':
            return email ? /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    "You signed in as ",
                    /*#__PURE__*/ _jsx("strong", {
                        className: "break-all text-foreground",
                        children: email
                    }),
                    ", but there's no Specra account for that email in ",
                    name,
                    ". Ask your Specra admin to invite you — then sign in again."
                ]
            }) : `There's no Specra account for that sign-in in ${name}. Ask your Specra admin to invite you — then sign in again.`;
        case 'domain_not_allowed':
            return `${name} doesn't allow sign-ins from ${domain || 'that email domain'}. Ask your Specra admin to add it, or sign in with an address ${name} already allows.`;
        case 'inactive':
            return email ? /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    "You signed in as ",
                    /*#__PURE__*/ _jsx("strong", {
                        className: "break-all text-foreground",
                        children: email
                    }),
                    ". Your Specra admin can reactivate the account — then you can sign in again."
                ]
            }) : 'Ask your Specra admin to reactivate it — then sign in again.';
        case 'provider_error':
            return "That didn't work this time. Try signing in again — if it keeps happening, ask your Specra admin.";
        default:
            return 'Something went wrong while signing you in. Try again, or ask your Specra admin if you keep seeing this.';
    }
}
export default function SignInAccessPage() {
    const [searchParams] = useSearchParams();
    const reason = parseAccessReason(searchParams.get('reason'));
    const email = parseAccessEmail(searchParams.get('email'));
    const org = parseAccessOrg(searchParams.get('org'));
    const provider = parseAccessProvider(searchParams.get('provider'));
    const domain = emailDomain(email);
    const headline = headlineFor(reason, org, domain);
    // Only an admin can fix a missing, refused or switched-off account; a
    // provider hiccup just needs another go.
    const adminCanHelp = reason === 'no_account' || reason === 'domain_not_allowed' || reason === 'inactive';
    const [copyStatus, setCopyStatus] = useState('idle');
    const adminMessage = useMemo(()=>adminInviteMessage(email, org), [
        email,
        org
    ]);
    const handleCopy = async ()=>{
        const ok = await copyToClipboard(adminMessage);
        setCopyStatus(ok ? 'copied' : 'failed');
    };
    const hint = provider === 'microsoft' ? "Signed in with the wrong Microsoft account? Use 'Try a different account'." : "Signed in with the wrong account? Use 'Try a different account'.";
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(Helmet, {
                children: /*#__PURE__*/ _jsx("title", {
                    children: "You don't have access yet - Specra"
                })
            }),
            /*#__PURE__*/ _jsx("div", {
                className: "flex min-h-dvh w-full items-start justify-center overflow-x-hidden bg-background px-4 py-10 md:items-center",
                "data-testid": Testids.signInAccess.page,
                children: /*#__PURE__*/ _jsx(SpecraCard, {
                    className: "w-full min-w-0 max-w-lg",
                    elevation: "sm",
                    children: /*#__PURE__*/ _jsxs(SpecraCardContent, {
                        className: "flex flex-col items-center gap-[var(--space-5)] px-6 py-[var(--space-8)] text-center sm:px-10",
                        children: [
                            /*#__PURE__*/ _jsx(SpecraWordmark, {
                                height: 28
                            }),
                            /*#__PURE__*/ _jsx(AccessIllustration, {}),
                            /*#__PURE__*/ _jsxs("div", {
                                className: "flex flex-col items-center gap-[var(--space-2)]",
                                children: [
                                    /*#__PURE__*/ _jsx(SpecraHeading, {
                                        as: "h1",
                                        "data-testid": Testids.signInAccess.headline,
                                        size: "md",
                                        children: headline
                                    }),
                                    /*#__PURE__*/ _jsx(SpecraText, {
                                        as: "p",
                                        className: "max-w-md",
                                        "data-testid": Testids.signInAccess.body,
                                        size: "body-sm",
                                        tone: "muted",
                                        children: /*#__PURE__*/ _jsx(AccessBody, {
                                            domain: domain,
                                            email: email,
                                            org: org,
                                            reason: reason
                                        })
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                className: "flex w-full max-w-sm flex-col items-center gap-[var(--space-3)]",
                                children: [
                                    adminCanHelp ? /*#__PURE__*/ _jsx(SpecraButton, {
                                        "data-testid": Testids.signInAccess.copyRequest,
                                        fullWidth: true,
                                        onClick: handleCopy,
                                        children: copyStatus === 'copied' ? 'Copied — send it to your admin' : 'Copy request for my admin'
                                    }) : null,
                                    adminCanHelp && copyStatus === 'failed' ? /*#__PURE__*/ _jsx(SpecraText, {
                                        as: "p",
                                        className: "w-full select-all rounded-[var(--radius-sm)] border border-border bg-muted px-3 py-2 text-left",
                                        "data-testid": Testids.signInAccess.copyFallback,
                                        size: "body-sm",
                                        children: adminMessage
                                    }) : null,
                                    adminCanHelp ? null : /*#__PURE__*/ _jsx(Link, {
                                        className: cn(specraButtonVariants(), 'w-full'),
                                        "data-testid": Testids.signInAccess.tryAgain,
                                        to: "/login",
                                        children: "Try again"
                                    }),
                                    /*#__PURE__*/ _jsx("div", {
                                        "aria-live": "polite",
                                        className: "sr-only",
                                        "data-testid": Testids.signInAccess.copyStatus,
                                        children: copyStatus === 'copied' ? 'Copied. Send that to your admin.' : copyStatus === 'failed' ? 'Copy failed. The request is shown below the button; select it and copy it.' : ''
                                    }),
                                    /*#__PURE__*/ _jsx(Link, {
                                        className: cn(specraButtonVariants({
                                            variant: 'secondary'
                                        }), 'w-full'),
                                        "data-testid": Testids.signInAccess.tryDifferentAccount,
                                        to: "/login?prompt=select_account",
                                        children: "Try a different account"
                                    }),
                                    adminCanHelp ? /*#__PURE__*/ _jsx(SpecraLink, {
                                        "data-testid": Testids.signInAccess.backToSignIn,
                                        href: "/login",
                                        children: "Back to sign in"
                                    }) : null,
                                    /*#__PURE__*/ _jsx(SpecraText, {
                                        as: "p",
                                        className: "max-w-xs",
                                        "data-testid": Testids.signInAccess.hint,
                                        size: "caption",
                                        tone: "muted",
                                        children: hint
                                    })
                                ]
                            })
                        ]
                    })
                })
            })
        ]
    });
}
